import { useEffect, useRef, useState } from "react";
import { Form, Input, Skeleton } from "../../components/ui";
import {
  useAddParent,
  useGetParentById,
  useUpdateParent,
} from "../../query/parentQuery";
import { useUpdateChild } from "../../query/childQuery";
import FormPhotoDrop from "../../components/FormPhotoDrop";
import ParentChildrenAssign from "../../components/ParentChildrenAssign";
import swal from "../../utils/swal";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { PARENTS, UPLOADS_URL } from "../../config/constants";
import { Post } from "../../config/api/post";
import {
  applyAddPassword,
  applyUpdatePassword,
  makeParentPassword,
} from "../../config/helpers";
import "../teachers/addTeacher.css";

function AddParent() {
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { mutate: addParent, isPending: isAdding } = useAddParent();
  const { mutate: updateParent, isPending: isUpdating } = useUpdateParent();
  const { mutateAsync: updateChildAsync } = useUpdateChild();
  const { data: parent, isLoading } = useGetParentById(isEdit ? id : undefined);
  const intentRef = useRef("ACTIVE");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [stagedChildren, setStagedChildren] = useState([]);
  const isMutating = isAdding || isUpdating;

  useEffect(() => {
    if (!isEdit || !parent) return;

    form.setFieldsValue({
      fatherFirstName: parent.fatherFirstName || "",
      fatherLastName: parent.fatherLastName || "",
      motherFirstName: parent.motherFirstName || "",
      motherLastName: parent.motherLastName || "",
      email: parent.email || "",
      phone: parent.phone || "",
      address: parent.address || "",
      city: parent.city || "",
      state: parent.state || "",
      zip: parent.zip || "",
      parentId: parent.parentId || "",
    });

    if (parent.image) {
      setImagePreview(`${UPLOADS_URL}/${parent.image}`);
    }
    intentRef.current = parent.status === "INACTIVE" ? "INACTIVE" : "ACTIVE";
  }, [isEdit, parent, form]);

  const buildFormData = (values) => {
    const formObject = new FormData();
    if (imageFile) formObject.append("image", imageFile);
    formObject.append("fatherFirstName", values.fatherFirstName || "");
    formObject.append("fatherLastName", values.fatherLastName || "");
    formObject.append("motherFirstName", values.motherFirstName || "");
    formObject.append("motherLastName", values.motherLastName || "");
    formObject.append("email", values.email || "");
    formObject.append("phone", values.phone || "");
    formObject.append("address", values.address || "");
    formObject.append("city", values.city || "");
    formObject.append("state", values.state || "");
    formObject.append("zip", values.zip || "");
    formObject.append(
      "status",
      intentRef.current === "INACTIVE" ? "INACTIVE" : "ACTIVE"
    );
    return formObject;
  };

  const linkStagedChildren = async (parentId) => {
    if (!parentId || !stagedChildren.length) return;
    await Promise.all(
      stagedChildren.map((child) => {
        const formObject = new FormData();
        formObject.append("parent", parentId);
        return updateChildAsync({ childId: child._id, data: formObject });
      })
    );
  };

  const onFinish = (values) => {
    if (!isEdit && !imageFile) {
      swal("Oops!", "Please upload a photo", "error");
      return;
    }

    const formObject = buildFormData(values);

    if (isEdit) {
      if (values.parentId) formObject.append("parentId", values.parentId);

      if (values.newPassword || values.confirmPassword) {
        if (!values.newPassword || !values.confirmPassword) {
          swal("Oops!", "Please enter and confirm the new password", "error");
          return;
        }
        if (values.newPassword !== values.confirmPassword) {
          swal("Oops!", "Passwords do not match", "error");
          return;
        }
      }

      updateParent(
        { parentId: id, data: formObject },
        {
          onSuccess: async () => {
            if (!values.newPassword) {
              swal("Success", "Parent updated successfully", "success");
              navigate(`/parents/${id}`);
              return;
            }

            try {
              const response = await Post(
                PARENTS.resetParentPassword + id,
                { password: values.newPassword },
                token
              );
              if (response?.data?.status) {
                swal("Success", "Parent updated and password changed successfully", "success");
                navigate(`/parents/${id}`);
              } else {
                swal(
                  "Partial success",
                  response?.data?.message ||
                    response?.response?.data?.message ||
                    "Profile saved but password update failed",
                  "warning"
                );
                navigate(`/parents/${id}`);
              }
            } catch (error) {
              swal(
                "Partial success",
                error?.message || "Profile saved but password update failed",
                "warning"
              );
              navigate(`/parents/${id}`);
            }
          },
          onError: (error) => {
            swal("Error", error?.message || "Failed to update parent", "error");
          },
        }
      );
      return;
    }

    const password = values.password || makeParentPassword();
    formObject.append("password", password);

    addParent(
      { data: formObject },
      {
        onSuccess: async (result) => {
          const payload = result?.data || result || {};
          const parentRecord = payload.parent || payload;
          const drafted = intentRef.current === "INACTIVE";
          const lines = [
            drafted ? "Parent saved as inactive" : "Parent added successfully",
          ];
          if (parentRecord?.parentId) {
            lines.push(`Parent ID: ${parentRecord.parentId}`);
          }
          if (!values.password) {
            lines.push(`Password: ${password}`);
          }

          try {
            if (parentRecord?._id && stagedChildren.length) {
              await linkStagedChildren(parentRecord._id);
              lines.push(`Linked ${stagedChildren.length} student(s)`);
            }
          } catch (error) {
            lines.push("Parent saved, but some children could not be linked");
          }

          swal("Success", lines.join("\n"), "success");
          navigate("/parents");
        },
        onError: (error) => {
          swal("Error", error?.message || "Failed to add parent", "error");
        },
      }
    );
  };

  if (isEdit && isLoading) {
    return (
      <div className="tf-page">
        <Skeleton active />
      </div>
    );
  }

  return (
    <div className="tf-page">
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
        <section className="tf-card">
          <h2 className="tf-card-head">Personal Details</h2>
          <div className="tf-card-body">
            <FormPhotoDrop
              preview={imagePreview}
              required={!isEdit}
              alt="Parent preview"
              onFile={(file) => {
                setImageFile(file);
                setImagePreview((prev) => {
                  if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
                  return URL.createObjectURL(file);
                });
              }}
            />

            <div className="tf-field">
              <p className="tf-label">
                Father First Name <span>*</span>
              </p>
              <Form.Item
                name="fatherFirstName"
                className="tf-item mb-0"
                rules={[
                  { required: true, message: "Please enter father's first name" },
                  { min: 3, message: "Name must be at least 3 characters" },
                ]}
              >
                <Input placeholder="John" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Father Last Name</p>
              <Form.Item name="fatherLastName" className="tf-item mb-0">
                <Input placeholder="Smith" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Mother First Name <span>*</span>
              </p>
              <Form.Item
                name="motherFirstName"
                className="tf-item mb-0"
                rules={[
                  { required: true, message: "Please enter mother's first name" },
                  { min: 3, message: "Name must be at least 3 characters" },
                ]}
              >
                <Input placeholder="Jane" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Mother Last Name</p>
              <Form.Item name="motherLastName" className="tf-item mb-0">
                <Input placeholder="Smith" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Email <span>*</span>
              </p>
              <Form.Item
                name="email"
                className="tf-item mb-0"
                rules={[
                  { required: true, message: "Please enter email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input placeholder="parent@mail.com" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Phone <span>*</span>
              </p>
              <Form.Item
                name="phone"
                className="tf-item mb-0"
                rules={[
                  { required: true, message: "Please enter phone" },
                  { min: 9, message: "Please enter a valid phone number" },
                ]}
              >
                <Input placeholder="+1234567890" />
              </Form.Item>
            </div>

            <div className="tf-field is-full">
              <p className="tf-label">Address</p>
              <Form.Item name="address" className="tf-item mb-0">
                <Input.TextArea className="is-short" placeholder="Enter address" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">City</p>
              <Form.Item name="city" className="tf-item mb-0">
                <Input placeholder="City" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">State</p>
              <Form.Item name="state" className="tf-item mb-0">
                <Input placeholder="State" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Zip Code</p>
              <Form.Item name="zip" className="tf-item mb-0">
                <Input placeholder="12190" />
              </Form.Item>
            </div>
          </div>
        </section>

        <ParentChildrenAssign
          parentId={isEdit ? id : undefined}
          assignedChildren={parent?.childrens || []}
          stagedChildren={stagedChildren}
          onStagedChange={setStagedChildren}
          disabled={isMutating}
        />

        <section className="tf-card">
          <h2 className="tf-card-head">Account</h2>
          <div className="tf-card-body">
            {isEdit ? (
              <div className="tf-field">
                <p className="tf-label">Parent ID</p>
                <Form.Item name="parentId" className="tf-item mb-0">
                  <Input disabled placeholder="Parent ID" />
                </Form.Item>
              </div>
            ) : null}

            {!isEdit ? (
              <div className="tf-field is-full">
                <p className="tf-label">Password</p>
                <div className="tf-password-row">
                  <Form.Item name="password" className="tf-item mb-0">
                    <Input.Password placeholder="Leave blank to auto-generate" />
                  </Form.Item>
                  <button
                    type="button"
                    className="tf-gen"
                    onClick={() => applyAddPassword(form, makeParentPassword())}
                  >
                    Generate
                  </button>
                </div>
                <p className="tf-hint">
                  If you leave this empty, a temporary password is created and shown after submit.
                </p>
              </div>
            ) : (
              <div className="tf-field is-full">
                <p className="tf-label">Update Password</p>
                <div className="tf-password-row">
                  <Form.Item name="newPassword" className="tf-item mb-0">
                    <Input.Password placeholder="Leave blank to keep current password" />
                  </Form.Item>
                  <button
                    type="button"
                    className="tf-gen"
                    onClick={() => applyUpdatePassword(form, makeParentPassword())}
                  >
                    Generate
                  </button>
                </div>
                <Form.Item
                  name="confirmPassword"
                  className="tf-item mb-0"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const next = getFieldValue("newPassword");
                        if (!next && !value) return Promise.resolve();
                        if (!value) {
                          return Promise.reject(new Error("Please confirm the new password"));
                        }
                        if (next === value) return Promise.resolve();
                        return Promise.reject(new Error("Passwords do not match"));
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder="Confirm new password" />
                </Form.Item>
                <p className="tf-hint">
                  Use this only when changing the login password. Leave both fields empty to keep the current one.
                </p>
              </div>
            )}
          </div>
        </section>

        <div className="tf-actions">
          <button
            type="submit"
            className="tf-btn is-ghost"
            disabled={isMutating}
            onClick={() => {
              intentRef.current = "INACTIVE";
            }}
          >
            {isMutating && intentRef.current === "INACTIVE" ? "Saving..." : "Save as Draft"}
          </button>
          <button
            type="submit"
            className="tf-btn is-primary"
            disabled={isMutating}
            onClick={() => {
              intentRef.current = "ACTIVE";
            }}
          >
            {isMutating && intentRef.current === "ACTIVE"
              ? "Saving..."
              : isEdit
              ? "Save"
              : "Submit"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default AddParent;
